import { useState, useMemo, useEffect, useRef } from 'react';
import { format, fromUnixTime, parseISO } from 'date-fns';
import {
  useTimelines,
  useTimeline,
  useBuildTimeline,
  useDeleteTimeline,
  useTimelineCatalysts,
  getTimelineExportUrl,
} from '@/hooks/useTimelines';
import { useOrganisations } from '@/hooks/useOrganisations';
import { useRegionSearch } from '@/hooks/useRegionSearch';
import { formatCount } from '@/lib/utils';

const EVENT_TYPES = [
  'Church Service',
  'One-on-One',
  'Small Group',
  'Community Outreach',
  'Youth Event',
  'Prayer Meeting',
  'Workshop / Training',
  'Medical / Relief',
  'Online Event',
];

// ── Shared close button ───────────────────────────────────────────────────────

function CloseButton({ onClick }) {
  return (
    <button onClick={onClick} className="rounded-lg p-1.5 text-neutral-500 hover:bg-parchment">
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

// ── Region autocomplete input ─────────────────────────────────────────────────

function RegionAutocompleteInput({ value, onChange, onGeoSelect }) {
  const { handleChange: setGeoQuery, suggestions, isFetching } = useRegionSearch();
  const [inputText, setInputText] = useState(value ?? '');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!value) setInputText('');
  }, [value]);

  const handleInput = (e) => {
    const v = e.target.value;
    setInputText(v);
    setGeoQuery(v);
    onChange(v);
    // Free-typed text has no geo context — clear any previously selected bbox.
    onGeoSelect?.(null);
    setOpen(true);
  };

  const handleSelect = (s) => {
    setInputText(s.name);
    onChange(s.name);
    // Pass bbox + center so the build payload can include spatial context.
    onGeoSelect?.({ bbox: s.bbox, center: s.center, name: s.name });
    setOpen(false);
  };

  useEffect(() => {
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={inputText}
        onChange={handleInput}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder="e.g. East Africa, Kenya"
        className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink placeholder:text-neutral-400 focus:border-[#c0392b] focus:outline-none"
      />
      {isFetching && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-neutral-300 border-t-[#c0392b]" />
        </div>
      )}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-neutral-200 bg-document shadow-lg">
          {suggestions.map((s) => (
            <li
              key={s.id}
              onMouseDown={() => handleSelect(s)}
              className="cursor-pointer px-3 py-2.5 text-sm text-ink hover:bg-parchment"
            >
              {s.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Build form ────────────────────────────────────────────────────────────────

function BuildTimelineForm({ onClose }) {
  const buildTimeline = useBuildTimeline();
  const { data: orgs = [], isLoading: orgsLoading } = useOrganisations();

  const [form, setForm] = useState({
    label: '',
    date_from: '',
    date_to: '',
    event_types: [],
    org_id: '',
  });
  // Spatial context captured when the user picks from the geocoder dropdown.
  const [geoContext, setGeoContext] = useState(null);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleEventType = (type) =>
    setForm((p) => ({
      ...p,
      event_types: p.event_types.includes(type)
        ? p.event_types.filter((t) => t !== type)
        : [...p.event_types, type],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { label: form.label };
    if (form.date_from) payload.date_from = form.date_from;
    if (form.date_to) payload.date_to = form.date_to;
    if (form.event_types.length) payload.event_types = form.event_types;
    if (form.org_id) payload.org_id = Number(form.org_id);
    // Include geo bounds when the user picked a real place from the geocoder.
    if (geoContext?.bbox) {
      payload.geo_bbox = {
        min_lon: geoContext.bbox[0],
        min_lat: geoContext.bbox[1],
        max_lon: geoContext.bbox[2],
        max_lat: geoContext.bbox[3],
      };
    } else if (geoContext?.center) {
      // Some places (e.g. small towns) have no bbox — fall back to center point.
      payload.geo_center = { lon: geoContext.center[0], lat: geoContext.center[1] };
    }
    try {
      await buildTimeline.mutateAsync(payload);
      onClose();
    } catch {
      // Toast shown by hook
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-2xl border border-neutral-200 bg-document shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-ink">Build New Timeline</h2>
          <CloseButton onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-neutral-600">
              Label <span className="text-[#003cc5]">*</span>
            </label>
            <input
              type="text"
              required
              maxLength={255}
              value={form.label}
              onChange={(e) => setField('label', e.target.value)}
              placeholder="e.g. East Africa Q1 2025"
              className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink placeholder:text-neutral-400 focus:border-[#003cc5] focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-neutral-600">Region</label>
            <RegionAutocompleteInput
              value={form.region}
              onChange={(v) => setField('region', v)}
              onGeoSelect={setGeoContext}
            />
            {geoContext?.bbox ? (
              <p className="mt-1 text-xs text-emerald-600">
                ✓ Spatial bounds locked — events within this area will be included
              </p>
            ) : form.region ? (
              <p className="mt-1 text-xs text-neutral-400">
                Select a suggestion to enable geographic matching
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-neutral-600">From</label>
              <input
                type="date"
                value={form.date_from}
                onChange={(e) => setField('date_from', e.target.value)}
                className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink focus:border-[#003cc5] focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-neutral-600">To</label>
              <input
                type="date"
                value={form.date_to}
                onChange={(e) => setField('date_to', e.target.value)}
                className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink focus:border-[#003cc5] focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-neutral-600">Organisation</label>
            {orgsLoading ? (
              <div className="h-9 animate-skeleton rounded-lg bg-paper" />
            ) : orgs.length > 0 ? (
              <select
                value={form.org_id}
                onChange={(e) => setField('org_id', e.target.value)}
                className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink focus:border-[#c0392b] focus:outline-none"
              >
                <option value="">All organisations</option>
                {orgs.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                min="1"
                value={form.org_id}
                onChange={(e) => setField('org_id', e.target.value)}
                placeholder="Organisation ID"
                className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink placeholder:text-neutral-400 focus:border-[#c0392b] focus:outline-none"
              />
            )}
          </div>

          <div>
            <label className="mb-2 block text-xs font-medium text-neutral-600">
              Event types <span className="text-neutral-400">(optional filter)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {EVENT_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleEventType(type)}
                  className={`rounded-full border px-3 py-1 text-xs transition ${
                    form.event_types.includes(type)
                      ? 'border-[#003cc5] bg-red-50 text-[#003cc5]'
                      : 'border-neutral-200 text-neutral-500 hover:border-neutral-400'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-neutral-200 bg-paper px-4 py-2 text-sm text-neutral-600 transition hover:bg-paper"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!form.label.trim() || buildTimeline.isPending}
              className="rounded-xl bg-[#003cc5] px-6 py-2 text-sm font-semibold text-parchment transition hover:bg-[#0036b1] disabled:opacity-50"
            >
              {buildTimeline.isPending ? 'Building…' : 'Build Timeline'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Contributors tab ──────────────────────────────────────────────────────────

function formatEventDate(raw) {
  if (!raw) return '—';
  try {
    if (typeof raw === 'number') return format(fromUnixTime(raw), 'MMM d, yyyy');
    return format(parseISO(raw), 'MMM d, yyyy');
  } catch {
    return String(raw);
  }
}

function ContributorsTab({ timeline }) {
  const [expanded, setExpanded] = useState(null);

  const filterParams = useMemo(
    () => {
      const fp = timeline.filter_params ?? {};
      const hasSpatial = fp.candidate_ids?.length > 0 || fp.geo_bbox;
      return {
        // If spatial IDs were pre-computed at build time, use them directly —
        // this avoids re-running PostGIS and works for already-built timelines.
        candidate_ids: fp.candidate_ids,
        // Fall back to bbox re-query if candidate_ids aren't stored.
        geo_bbox: !fp.candidate_ids?.length ? fp.geo_bbox : undefined,
        date_from: fp.date_from,
        date_to: fp.date_to,
        organization_id: fp.org_id,
        event_types: fp.event_types,
        // Skip text region filter when spatial context is available.
        region: hasSpatial ? undefined : fp.region,
      };
    },
    [timeline.filter_params],
  );

  const { data: catalysts = [], isLoading } = useTimelineCatalysts(filterParams, {
    enabled: !!timeline,
  });

  const contributors = useMemo(() => {
    const map = new Map();
    catalysts.forEach((c) => {
      const uid = c.author_uid ?? c.uid ?? c.author_id ?? null;
      const name = c.author_name ?? c.author ?? (uid ? `User #${uid}` : 'Unknown');
      const key = uid ?? name;
      if (!map.has(key)) {
        map.set(key, { uid, name, events: [], totalLives: 0, eventTypes: new Set() });
      }
      const entry = map.get(key);
      entry.events.push(c);
      entry.totalLives += Number(
        c.lives_affected ?? c.field_lives_affected ?? c.impact_count ?? c.field_impact_count ?? 0,
      );
      const et = c.event_type ?? c.field_event_type;
      if (et) entry.eventTypes.add(et);
    });
    return Array.from(map.values())
      .map((e) => ({ ...e, eventTypes: Array.from(e.eventTypes) }))
      .sort((a, b) => b.events.length - a.events.length);
  }, [catalysts]);

  if (isLoading) {
    return (
      <div className="space-y-2 py-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-11 animate-skeleton rounded-lg bg-paper" />
        ))}
      </div>
    );
  }

  if (contributors.length === 0) {
    return (
      <p className="py-8 text-center text-sm italic text-neutral-400">
        No contributor data found for this timeline's filters.
      </p>
    );
  }

  return (
    <div>
      {catalysts.length === 100 && (
        <p className="mb-3 rounded-lg bg-paper px-3 py-2 text-xs text-neutral-500">
          Showing first 100 events. Use CSV export for the complete dataset.
        </p>
      )}
      <div className="overflow-hidden rounded-xl border border-neutral-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 bg-paper text-left text-xs uppercase tracking-wider text-neutral-500">
              <th className="px-4 py-3">Contributor</th>
              <th className="px-4 py-3 text-right">Events</th>
              <th className="px-4 py-3 text-right">Lives</th>
              <th className="px-4 py-3">Event Types</th>
              <th className="px-4 py-3 w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {contributors.map((c) => {
              const key = c.uid ?? c.name;
              const isExpanded = expanded === key;
              return (
                <>
                  <tr
                    key={key}
                    className="cursor-pointer hover:bg-parchment"
                    onClick={() => setExpanded(isExpanded ? null : key)}
                  >
                    <td className="px-4 py-3 font-medium text-ink">{c.name}</td>
                    <td className="px-4 py-3 text-right text-neutral-600">
                      {formatCount(c.events.length)}
                    </td>
                    <td className="px-4 py-3 text-right text-neutral-600">
                      {formatCount(c.totalLives)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {c.eventTypes.slice(0, 3).map((et) => (
                          <span
                            key={et}
                            className="rounded-full border border-neutral-200 bg-paper px-2 py-0.5 text-xs text-neutral-500"
                          >
                            {et}
                          </span>
                        ))}
                        {c.eventTypes.length > 3 && (
                          <span className="rounded-full border border-neutral-200 bg-paper px-2 py-0.5 text-xs text-neutral-400">
                            +{c.eventTypes.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-400">
                      <svg
                        className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${key}-events`} className="bg-parchment">
                      <td colSpan={5} className="px-4 pb-3 pt-1">
                        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-document">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-neutral-200 text-left text-neutral-400">
                                <th className="px-3 py-2">Date</th>
                                <th className="px-3 py-2">Event Type</th>
                                <th className="px-3 py-2">Region</th>
                                <th className="px-3 py-2 text-right">Lives</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-100">
                              {c.events.map((ev, i) => {
                                const evDate =
                                  ev.event_date ?? ev.field_event_date ?? ev.created ?? null;
                                const evType = ev.event_type ?? ev.field_event_type ?? '—';
                                const evRegion = ev.region ?? ev.field_region ?? '—';
                                const evLives = Number(
                                  ev.lives_affected ??
                                    ev.field_lives_affected ??
                                    ev.impact_count ??
                                    ev.field_impact_count ??
                                    0,
                                );
                                return (
                                  <tr key={ev.id ?? ev.uuid ?? i} className="hover:bg-parchment">
                                    <td className="px-3 py-2 text-neutral-500">
                                      {formatEventDate(evDate)}
                                    </td>
                                    <td className="px-3 py-2 text-ink">{evType}</td>
                                    <td className="px-3 py-2 text-neutral-500">{evRegion}</td>
                                    <td className="px-3 py-2 text-right text-neutral-500">
                                      {evLives > 0 ? formatCount(evLives) : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Narrative viewer ──────────────────────────────────────────────────────────

function TimelineViewer({ id, onClose }) {
  const { data: timeline, isLoading } = useTimeline(id);
  const [activeTab, setActiveTab] = useState('narrative');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="flex w-full max-w-3xl flex-col rounded-2xl border border-neutral-200 bg-document shadow-2xl max-h-[88vh]">

        {/* Header */}
        <div className="flex items-start justify-between border-b border-neutral-200 px-6 py-4 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-ink">
              {timeline?.label ?? 'Timeline'}
            </h2>
            {timeline && (
              <p className="mt-0.5 text-xs text-neutral-500">
                {formatCount(timeline.total_events ?? 0)} events ·{' '}
                {formatCount(timeline.total_lives_affected ?? 0)} lives affected
                {timeline.filter_params?.region && (
                  <> · {timeline.filter_params.region}</>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {id && (
              <>
                <a
                  href={getTimelineExportUrl(id, 'pdf')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs text-neutral-600 transition hover:border-neutral-400"
                >
                  ↓ PDF
                </a>
                <a
                  href={getTimelineExportUrl(id, 'csv')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs text-neutral-600 transition hover:border-neutral-400"
                >
                  ↓ CSV
                </a>
              </>
            )}
            <CloseButton onClick={onClose} />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-neutral-200 px-6 pt-3 pb-0 shrink-0">
          {['narrative', 'contributors'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`rounded-t-lg px-4 py-2 text-xs font-medium capitalize transition ${
                activeTab === tab
                  ? 'border border-b-0 border-neutral-200 bg-document text-ink -mb-px'
                  : 'text-neutral-500 hover:text-ink'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-6 animate-skeleton rounded bg-paper" />
              ))}
            </div>
          ) : activeTab === 'narrative' ? (
            timeline?.generated_narrative ? (
              <div
                className="narrative-content"
                dangerouslySetInnerHTML={{ __html: timeline.generated_narrative }}
              />
            ) : (
              <p className="text-sm italic text-neutral-500">
                No narrative generated. Status: {timeline?.status ?? 'unknown'}
              </p>
            )
          ) : (
            timeline && <ContributorsTab timeline={timeline} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function TimelinesPage() {
  const { data: timelines = [], isLoading } = useTimelines();
  const deleteTimeline = useDeleteTimeline();

  const [showBuildForm, setShowBuildForm] = useState(false);
  const [viewingId, setViewingId] = useState(null);

  const handleDelete = (id, label) => {
    if (window.confirm(`Delete timeline "${label}"? This cannot be undone.`)) {
      deleteTimeline.mutate(id);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Timelines</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Narrative reports generated from filtered Catalyst sets
          </p>
        </div>
        <button
          onClick={() => setShowBuildForm(true)}
          className="rounded-xl bg-[#003cc5] px-4 py-2 text-sm font-semibold text-parchment transition hover:bg-[#0036b1]"
        >
          + Build Timeline
        </button>
      </div>

      {/* Timeline list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-skeleton rounded-xl bg-paper" />
          ))}
        </div>
      ) : timelines.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-200 py-24 text-center">
          <p className="text-neutral-500">No timelines yet.</p>
          <button
            onClick={() => setShowBuildForm(true)}
            className="mt-3 text-sm text-[#003cc5] hover:underline"
          >
            Build your first timeline →
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-paper">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wider text-neutral-500">
                <th className="px-5 py-3">Label</th>
                <th className="px-5 py-3">Region</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Events</th>
                <th className="px-5 py-3 text-right">Lives</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {timelines.map((tl) => (
                <tr key={tl.id} className="hover:bg-parchment">
                  <td className="max-w-[180px] truncate px-5 py-3 font-medium text-ink">
                    {tl.label}
                  </td>
                  <td className="max-w-[140px] truncate px-5 py-3 text-neutral-500">
                    {tl.filter_params?.region ?? '—'}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`rounded-full border px-2 py-0.5 text-xs capitalize ${
                        tl.status === 'generated'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-neutral-200 bg-parchment text-neutral-500'
                      }`}
                    >
                      {tl.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right text-neutral-500">
                    {formatCount(tl.total_events ?? 0)}
                  </td>
                  <td className="px-5 py-3 text-right text-neutral-500">
                    {formatCount(tl.total_lives_affected ?? 0)}
                  </td>
                  <td className="px-5 py-3 text-neutral-400">
                    {tl.created ? format(fromUnixTime(tl.created), 'MMM d, yyyy') : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setViewingId(tl.id)}
                        className="text-xs text-[#003cc5] hover:underline"
                      >
                        View
                      </button>
                      <a
                        href={getTimelineExportUrl(tl.id, 'pdf')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-neutral-500 hover:text-ink"
                      >
                        PDF
                      </a>
                      <a
                        href={getTimelineExportUrl(tl.id, 'csv')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-neutral-500 hover:text-ink"
                      >
                        CSV
                      </a>
                      <button
                        onClick={() => handleDelete(tl.id, tl.label)}
                        disabled={deleteTimeline.isPending}
                        className="text-xs text-[#003cc5] hover:underline disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Overlays */}
      {showBuildForm && <BuildTimelineForm onClose={() => setShowBuildForm(false)} />}
      {viewingId != null && (
        <TimelineViewer id={viewingId} onClose={() => setViewingId(null)} />
      )}
    </div>
  );
}
